import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { leaveService } from "@/features/leave/services/leaveService";
import { getAttachmentViewingUrl } from "@/shared/services/uploadService";
import { Button } from "@/shared/components/ui/Button";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Badge } from "@/shared/components/ui/Badge";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { CompanySearchSelect } from "@/shared/components/ui/CompanySearchSelect";
import { formatDateTime, formatDisplayName } from "@/shared/utils/formatters";
import type { LeaveSubmission, LeaveField } from "@/features/leave/types/leave";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url,
).toString();

const OFFICER_TYPES = ["officer", "admin"];

/** Renders the first page of a PDF as a canvas thumbnail. */
const PdfThumbnail = ({ url }: { url: string }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1);
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        const vp = page.getViewport({ scale: 1 });
        const scale = 480 / vp.width;
        const scaled = page.getViewport({ scale });
        canvas.width = scaled.width;
        canvas.height = scaled.height;
        await page.render({
          canvasContext: canvas.getContext("2d")!,
          viewport: scaled,
          canvas,
        }).promise;
      } catch {
        /* fail silently */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full rounded border border-gray-200 mt-2"
    />
  );
};

/** Fetches a presigned URL for an attachment and renders an open button + optional PDF preview. */
const AttachmentCell = ({ attachmentId }: { attachmentId: string }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!attachmentId) return;
    getAttachmentViewingUrl(attachmentId)
      .then((u) => setUrl(u))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [attachmentId]);

  if (loading) return <span className="text-xs text-gray-400">Loading…</span>;
  if (error || !url)
    return <span className="text-xs text-red-400">Unavailable</span>;

  const urlLower = url.toLowerCase();
  const isPdf =
    urlLower.includes(".pdf") ||
    urlLower.includes("content-type=application%2Fpdf") ||
    urlLower.includes("%2Fpdf");

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
        >
          <svg
            className="w-4 h-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
          Open file
        </button>
        {isPdf && (
          <button
            type="button"
            onClick={() => setShowPreview((p) => !p)}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            {showPreview ? "Hide preview" : "Preview PDF"}
          </button>
        )}
      </div>
      {isPdf && showPreview && <PdfThumbnail url={url} />}
    </div>
  );
};

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  sent: "info",
};

const STATUS_TABS = ["All", "sent"];

/** Right-side detail panel for a leave submission */
const LeaveDetailPanel = ({
  submission,
  fields,
  isOpen,
  onClose,
}: {
  submission: LeaveSubmission | null;
  fields: LeaveField[];
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  const resolvedFields = submission?.template_fields || fields;

  const getFieldDef = (key: string) =>
    resolvedFields.find((f) => f.id === key);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 shrink-0">
          <div className="min-w-0 pr-2">
            <div className="flex items-center gap-2 flex-wrap">
              {submission?.id !== undefined && (
                <span className="text-xs font-mono text-gray-400">
                  #{submission.id}
                </span>
              )}
              <Badge variant={statusVariant[submission?.status || ""] || "default"}>
                {submission ? t(`leave.status_${submission.status}`) : ""}
              </Badge>
              {submission?.template_title && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 truncate max-w-[200px]">
                  {submission.template_title}
                </span>
              )}
            </div>
            <h2 className="text-base font-semibold text-gray-900 mt-1 truncate">
              {submission
                ? formatDisplayName(
                    submission.last_name,
                    submission.first_name,
                    submission.middle_name,
                  )
                : ""}
            </h2>
            {submission?.email && (
              <p className="text-sm text-gray-500 mt-0.5">{submission.email}</p>
            )}
            {submission?.company_name && (
              <p className="text-xs font-medium text-indigo-600 mt-0.5">
                {submission.company_name}
              </p>
            )}
            {submission?.created_at && (
              <p className="text-xs text-gray-400 mt-0.5">
                {formatDateTime(submission.created_at)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {submission && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-2/5 px-3 py-2 text-left font-medium text-gray-600">
                      {t("leave.field")}
                    </th>
                    <th className="w-3/5 px-3 py-2 text-left font-medium text-gray-600">
                      {t("leave.answer")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(submission.answers).map(([key, value]) => {
                    const fieldDef = getFieldDef(key);
                    const isFile = fieldDef?.type === "file";
                    const label = fieldDef?.label || key;
                    return (
                      <tr key={key}>
                        <td className="px-3 py-2 text-gray-700 font-medium break-words align-top">
                          {label}
                        </td>
                        <td className="px-3 py-2 text-gray-600 break-all align-top">
                          {isFile && value ? (
                            <AttachmentCell attachmentId={value} />
                          ) : (
                            value || (
                              <span className="text-gray-400 italic">N/A</span>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const LeaveSubmissionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(
    userData?.userType?.toLowerCase() || "",
  );

  // Filters
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCompanyLabel, setSelectedCompanyLabel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [formTitleSearch, setFormTitleSearch] = useState("");

  // Data
  const [submissions, setSubmissions] = useState<LeaveSubmission[]>([]);
  const [fields, setFields] = useState<LeaveField[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<LeaveSubmission | null>(null);
  const [page, setPage] = useState(0);

  const loadData = useCallback(
    async (companyId?: string, start?: string, end?: string) => {
      try {
        setLoading(true);
        const subsPromise = isOfficer
          ? leaveService.getSubmissions(companyId || undefined, start, end)
          : leaveService.getMySubmissions();
        const [subs, template] = await Promise.all([
          subsPromise,
          leaveService.getTemplate(companyId || undefined).catch(() => null),
        ]);
        setSubmissions(subs);
        if (template) setFields(template.fields);
      } catch (err) {
        console.error("Failed to load submissions:", err);
      } finally {
        setLoading(false);
      }
    },
    [isOfficer],
  );

  // Load on mount and whenever filters change
  useEffect(() => {
    loadData(
      selectedCompany || undefined,
      startDate || undefined,
      endDate || undefined,
    );
  }, [loadData, selectedCompany, startDate, endDate]);

  // Auto-open panel when navigated from a notification deep-link
  useEffect(() => {
    const targetId = (location.state as { submissionId?: string } | null)?.submissionId;
    if (!targetId || submissions.length === 0) return;
    const match = submissions.find(
      (s) =>
        s.submission_id === targetId ||
        (s.id !== undefined && String(s.id) === String(targetId)),
    );
    if (match) {
      setSelected(match);
      // Clear state so re-renders don't re-open the panel
      window.history.replaceState({}, "");
    }
  }, [submissions, location.state]);

  const handleCompanySelect = (companyId: string, label: string) => {
    setSelectedCompany(companyId);
    setSelectedCompanyLabel(label);
  };

  const handleClearFilters = () => {
    setSelectedCompany("");
    setSelectedCompanyLabel("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("All");
    setFormTitleSearch("");
  };

  const handleExport = () => {
    const rows = filtered;
    if (rows.length === 0) return;

    // Collect all unique fields across all submissions (handles multiple templates)
    const allFieldsMap = new Map<string, LeaveField>();
    for (const sub of rows) {
      const subFields = sub.template_fields || fields;
      for (const f of subFields) {
        if (!allFieldsMap.has(f.id)) allFieldsMap.set(f.id, f);
      }
    }
    const allFields = Array.from(allFieldsMap.values());

    const headers = [
      "Name",
      "Company",
      "Email",
      "Form Title",
      "Status",
      "Submitted",
      ...allFields.map((f) => f.label),
    ];
    const csvRows = [
      headers.join(","),
      ...rows.map((sub) => {
        const base = [
          `"${formatDisplayName(sub.last_name, sub.first_name, sub.middle_name)}"`,
          `"${sub.company_name || ""}"`,
          `"${sub.email}"`,
          `"${sub.template_title || ""}"`,
          sub.status,
          `"${formatDateTime(sub.created_at)}"`,
        ];
        const answers = allFields.map((f) => `"${sub.answers[f.id] || ""}"`);
        return [...base, ...answers].join(",");
      }),
    ];

    const blob = new Blob(["\uFEFF" + csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leave-submissions${selectedCompanyLabel ? `-${selectedCompanyLabel}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = submissions.filter((s) => {
    if (statusFilter !== "All" && s.status !== statusFilter) return false;
    if (
      formTitleSearch.trim() &&
      !(s.template_title || "")
        .toLowerCase()
        .includes(formTitleSearch.trim().toLowerCase())
    )
      return false;
    return true;
  });

  useEffect(() => { setPage(0); }, [statusFilter, selectedCompany, startDate, endDate, formTitleSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / 50));
  const pagedSubmissions = filtered.slice(page * 50, (page + 1) * 50);

  const hasFilters = !!(selectedCompany || startDate || endDate || formTitleSearch);

  return (
    <div>
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <button
          type="button"
          onClick={() => navigate("/leave")}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 active:text-gray-800 p-2 -ml-2 rounded-lg mb-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t("leave.title")}
        </button>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            {t("leave.submissions_title")}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? (
              <span className="text-gray-400">Loading...</span>
            ) : isOfficer ? (
              <>
                {submissions.length} submission
                {submissions.length !== 1 ? "s" : ""}
                {selectedCompanyLabel
                  ? ` for ${selectedCompanyLabel}`
                  : " across all companies"}
              </>
            ) : (
              <>
                {submissions.length} of your submission
                {submissions.length !== 1 ? "s" : ""}
              </>
            )}
          </p>
        </div>
        {isOfficer && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            disabled={filtered.length === 0}
          >
            <svg
              className="w-4 h-4 mr-1.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {t("leave.export_csv", "Export CSV")}
          </Button>
        )}
        </div>
      </div>

      {/* Filters panel (Officers only) */}
      {isOfficer && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 mb-4 space-y-4">
          {/* Row 1: Company + Form Title */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <CompanySearchSelect
                value={selectedCompany}
                onChange={handleCompanySelect}
                label={t("leave.select_company")}
                feature="company_form"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t("leave.form_title")}
              </label>
              <input
                type="text"
                value={formTitleSearch}
                onChange={(e) => setFormTitleSearch(e.target.value)}
                placeholder={t("leave.search_form_title")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          {/* Row 2: Date range */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          {hasFilters && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && <Spinner />}

      {/* Data content */}
      {!loading && (
        <>
          {/* Status filter tabs */}
          <div className="flex gap-2 mb-4 md:mb-6 flex-wrap overflow-x-auto pb-1 -mx-3 px-3 md:mx-0 md:px-0">
            {STATUS_TABS.map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-2 md:py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap active:scale-95 ${
                  statusFilter === status
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {t(`leave.status_${status.toLowerCase()}`)}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <EmptyState title={t("leave.no_submissions")} />
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-xs text-gray-500">
                    {t("settings.search_showing", {
                      from: page * 50 + 1,
                      to: Math.min((page + 1) * 50, filtered.length),
                      total: filtered.length,
                    })}
                  </p>
                </div>
                <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                        #
                      </th>
                      {!selectedCompany && (
                        <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                          {t("leave.company")}
                        </th>
                      )}
                      {isOfficer && (
                        <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                          {t("leave.applicant")}
                        </th>
                      )}
                      {isOfficer && (
                        <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                          {t("leave.email")}
                        </th>
                      )}
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                        {t("leave.form_title")}
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                        {t("leave.status")}
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                        {t("leave.submitted")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pagedSubmissions.map((sub) => (
                      <tr
                        key={sub.submission_id}
                        onClick={() => setSelected(sub)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono whitespace-nowrap">
                          {sub.id !== undefined ? `#${sub.id}` : "—"}
                        </td>
                        {!selectedCompany && (
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {sub.company_name || (
                              <span className="text-gray-400 italic">—</span>
                            )}
                          </td>
                        )}
                        {isOfficer && (
                          <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                            {formatDisplayName(sub.last_name, sub.first_name, sub.middle_name)}
                          </td>
                        )}
                        {isOfficer && (
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {sub.email}
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap max-w-[180px]">
                          {sub.template_title ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 truncate max-w-full">
                              {sub.template_title}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge
                            variant={statusVariant[sub.status] || "default"}
                          >
                            {t(`leave.status_${sub.status}`)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                          {formatDateTime(sub.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                    <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                      {t("settings.page_prev")}
                    </Button>
                    <span className="text-xs text-gray-500">{page + 1} / {totalPages}</span>
                    <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                      {t("settings.page_next")}
                    </Button>
                  </div>
                )}
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {pagedSubmissions.map((sub) => (
                  <button
                    key={sub.submission_id}
                    onClick={() => setSelected(sub)}
                    className="w-full text-left bg-white rounded-lg border border-gray-200 p-4 active:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {sub.id !== undefined && (
                          <span className="text-xs font-mono text-gray-400 shrink-0">
                            #{sub.id}
                          </span>
                        )}
                        {isOfficer ? (
                          <span className="font-medium text-gray-900 truncate">
                            {formatDisplayName(sub.last_name, sub.first_name, sub.middle_name)}
                          </span>
                        ) : (
                          <span className="font-medium text-gray-900 truncate">
                            {t(`leave.status_${sub.status}`)}
                          </span>
                        )}
                      </div>
                      <Badge variant={statusVariant[sub.status] || "default"}>
                        {t(`leave.status_${sub.status}`)}
                      </Badge>
                    </div>
                    {isOfficer && !selectedCompany && sub.company_name && (
                      <p className="text-xs font-medium text-indigo-600 mb-0.5">
                        {sub.company_name}
                      </p>
                    )}
                    {isOfficer && (
                      <p className="text-sm text-gray-500 truncate">
                        {sub.email}
                      </p>
                    )}
                    {sub.template_title && (
                      <p className="text-xs font-medium text-indigo-600 mt-1 truncate">
                        {sub.template_title}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDateTime(sub.created_at)}
                    </p>
                  </button>
                ))}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between py-2">
                    <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                      {t("settings.page_prev")}
                    </Button>
                    <span className="text-xs text-gray-500">{page + 1} / {totalPages}</span>
                    <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                      {t("settings.page_next")}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Detail Panel */}
      <LeaveDetailPanel
        submission={selected}
        fields={fields}
        isOpen={!!selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};
